import { ENV_CONFIG } from '../../../constants/envConfig';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import config from '../../App/config';
import { logError } from '../../global/utils/logger';
import { redirectWithLocale } from '../../global/utils/localeUrl';
import { getAuthToken, sendOtp } from '../api/apiRequest';
import { resolveLanguage } from '../../App/i18n';
import { BUSINESS_ACCOUNT, CASH_FLOW_PATH, CREATE_ACCOUNT_SCREEN, ERROR_SCREEN, SUCCESS_CODES } from '../constants';

export const navigateToPath = (navigate, path) => {
    navigate(`${config.pagePaths.guidedRegWorkflow}${path || ''}`,  { replace: true });
};

export const getEmailVerificationObject = () => {
    try {
        const { GR_CREATE_ACCOUNT: { emailverificationconfigs = '' } = {} } = ENV_CONFIG;
        return JSON.parse(emailverificationconfigs);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const getCreateAccountObject = () => {
    try {
        const { GR_CREATE_ACCOUNT: { createaccountconfigs = '' } = {} } = ENV_CONFIG;
        return JSON.parse(createaccountconfigs);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const saveReferer = () => {
    try {
        const referer = document.referrer;
        if (referer != '') {
            const refererPatterns = [
                '/cca',
                '/createaccount/personal',
                '/createaccount/language-selector',
                '/email-verify',
                '/convertProfile'
            ];
            const shouldStoreReferer = !refererPatterns.some(pattern => referer.includes(pattern));
            if (shouldStoreReferer) {
                sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.REFERER, referer);
            }
        }
    } catch (error) {
        logGRError(error);
    }
};

const isFrench = language => language.toLowerCase().startsWith('fr');

// The language selector is the only way to switch language, e.g. removing /fr-ca from the URL goes back to it
export const checkSelectedLanguage = () => {
    const selected = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SELECTED_LANGUAGE);
    if (selected && isFrench(selected) !== isFrench(resolveLanguage().language)) {
        redirectWithLocale(`${CREATE_ACCOUNT_SCREEN}${window.location.search}`, true);
    }
};

// Shared by the language selector and create account pages, since users can land on either one
export const storeGuidedRegQueryParams = () => {
    const params = new URLSearchParams(window?.location?.search);
    const { c, s, o, salesrepid, sfaccountnumber, type, email, ers } = Object.fromEntries(params.entries());

    if (c || o || s || sfaccountnumber || salesrepid || type === BUSINESS_ACCOUNT) {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.ACCOUNT_TYPE_PREFERENCE, BUSINESS_ACCOUNT);
    }
    if (type === BUSINESS_ACCOUNT && email) {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.NEW_USER_EMAIL, email.replace(/"/g, ''));
    }
    const salesrepId = s || salesrepid;
    if (salesrepId) {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.SALESREPID, salesrepId);
    }
    if (sfaccountnumber) {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.SFID, sfaccountnumber);
    }
    if (ers) {
        sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.ERS, ers);
    }
};

// `language` uses the customers API format (EN, FR-CA), matched case-insensitively against the authored locales
export const getPreselectedLanguage = (locales = []) => {
    const language = new URLSearchParams(window?.location?.search).get('language')?.toLowerCase();
    return locales.find(locale => locale.toLowerCase() === language) || '';
};

export const getLanguageSelectorObject = () => {
    try {
        const { GR_LANGUAGE_SELECTION: { languageselectorconfigs = '' } = {} } = ENV_CONFIG;
        return languageselectorconfigs ? JSON.parse(languageselectorconfigs) : {};
    } catch (error) {
        logError(error);
        return {};
    }
};

export const getCCAConfigObject = () => {
    try {
        const { CCA_CONFIG: { allconfig = {} } } = ENV_CONFIG;
        return JSON.parse(allconfig);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const getBasicAccountScreenConfigs = () => {
    try {
        const { GR_CREATE_ACCOUNT: { basicaccountscreenconfigs = '' } = {} } = ENV_CONFIG;
        return JSON.parse(basicaccountscreenconfigs);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const getBusinessAccountScreenConfigs = () => {
    try {
        const { GR_CREATE_ACCOUNT: { businessaccountscreenconfigs = '' } = {} } = ENV_CONFIG;
        return JSON.parse(businessaccountscreenconfigs);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const getCashToPodScreenConfigs = () => {
    try {
        const { GR_CREATE_ACCOUNT: { cashtopodscreenconfigs = '' } = {} } = ENV_CONFIG;
        return JSON.parse(cashtopodscreenconfigs);
    } catch (error) {
        logError(error);
        return {};
    }
};

export const addFormReloadWarning = isFormDirty => {
    window.onbeforeunload = () => {
        if (isFormDirty) {
            return 'You have unsaved changes. Are you sure you want to leave this page?';
        }
    };

    return () => {
        window.onbeforeunload = null;
    };
};

export const refreshTokenCall = async () => {
    try {
        const { data: { access_token: token } = {}, error: authError = {} , status: status} = (await getAuthToken()) || {};
        if (status === SUCCESS_CODES.SUCCESS_CODE_200) {
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.GR_TOKEN, token);
        } else {
            throw new Error(authError);
        }
    } catch (error) {
        logGRError(error);
    }
};

export const resendVerifyCode = async (emailOrNumber, type) => {
    try {
        await refreshTokenCall();
        const { data, status, error } = await sendOtp(emailOrNumber, type);
        return { data, status, error };
    } catch (error) {
        logGRError(error);
        return { data: null, error: error, status: null };
    }
};

export const redirectToCashFlow = () => {
    const url = window?.location?.protocol + '//' + window?.location?.host + CASH_FLOW_PATH;
    redirectWithLocale(url, true);
};

export const logGRError = error => {
    window.onbeforeunload = null;
    logError(error);
    redirectWithLocale(ERROR_SCREEN);
};

export const clearCache = () => {
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CLIENT_IP_ADDRESS);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.GR_TOKEN);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SALESREPID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.ACCOUNT_TYPE_PREFERENCE);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.NEW_USER_EMAIL);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SFID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.SESSIONID);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.USER_EXISTS);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.USER_PHONE_VERIFIED);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.ERS);
    sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_VERIFICATION_CODE);
};
